import { ChainId } from '@nominex/nomiswap-sdk'
import { getPoolsConfig, PoolCategory } from 'config/pools'
import bep20Abi from 'config/abi/IBep20Contract.json'
import multicall from 'utils/multicall'
import BigNumber from 'bignumber.js'
import { getBoostingServiceContract, getStakingServiceContract } from 'utils/contractHelpers'

export const fetchPoolsAllowance = async (account, chainId: ChainId) => {
  const poolsConfig = getPoolsConfig(chainId)
  const calls = poolsConfig.map((pool) => ({
    address: pool.stakingToken.address,
    name: 'allowance',
    params: [account, pool.contractAddress],
  }))
  const allowances = await multicall(bep20Abi, calls, chainId)

  return poolsConfig.reduce(
    (acc, { sousId }, index) => ({ ...acc, [sousId]: new BigNumber(allowances[index]).toJSON() }),
    {},
  )
}

export const fetchUserBalances = async (account, chainId: ChainId) => {
  const poolsConfig = getPoolsConfig(chainId)
  const calls = poolsConfig.map((pool) => ({
    address: pool.stakingToken.address,
    name: 'balanceOf',
    params: [account],
  }))

  const tokenBalancesRaw = await multicall(bep20Abi, calls, chainId)

  return poolsConfig.reduce(
    (acc, { sousId }, index) => ({ ...acc, [sousId]: new BigNumber(tokenBalancesRaw[index]).toJSON() }),
    {},
  )
}

export const fetchUserStakeBalances = async (account, chainId: ChainId) => {
  const poolsConfig = getPoolsConfig(chainId)
  const poolsCalls = poolsConfig.map(({ poolCategory, contractAddress }) => {
    switch (poolCategory) {
      case PoolCategory.CORE:
        return getStakingServiceContract(contractAddress, chainId).stakers(account)
      case PoolCategory.BOOSTING:
        return getBoostingServiceContract(contractAddress).stakers(account)
      default:
        return Promise.resolve({ amount: 0 })
    }
  })

  const stakedBalances = await Promise.all(poolsCalls)

  return poolsConfig.reduce(
    (acc, { sousId }, index) => ({
      ...acc,
      [sousId]: stakedBalances[index].amount.toString(),
    }),
    {},
  )
}

export const fetchUserPendingRewards = async (account: string, chainId: ChainId) => {
  const poolsConfig = getPoolsConfig(chainId)
  const poolsCalls = poolsConfig.map(({ poolCategory, contractAddress }) => {
    switch (poolCategory) {
      case PoolCategory.CORE:
        return getStakingServiceContract(contractAddress, chainId).getReward({ from: account })
      default:
        return Promise.resolve(0)
    }
  })

  const pendingRewards = await Promise.all(poolsCalls)

  return poolsConfig.reduce(
    (acc, { sousId }, index) => ({
      ...acc,
      [sousId]: pendingRewards[index].toString(),
    }),
    {},
  )
}

export const fetchUserBoostingInfo = async (account: string, chainId: ChainId) => {
  const poolsConfig = getPoolsConfig(chainId)
  const poolsCalls = poolsConfig
    .filter(({ poolCategory }) => poolCategory === PoolCategory.BOOSTING)
    .map(async ({ contractAddress, sousId }) => {
      const boostingServiceContract = getBoostingServiceContract(contractAddress)
      const [stakerInfo, rewardsInfo] = await Promise.all([
        boostingServiceContract.stakers(account),
        boostingServiceContract.callStatic.getAndUpdateStaker({ from: account }),
      ])

      return {
        sousId,
        boostingInfo: {
          amount: rewardsInfo.amount.toString(),
          boostingAmount: rewardsInfo.boostingAmount.toString(),
          initialBoostingRate: rewardsInfo.initialBoostingRate.toString(),
          initialCompoundRate: rewardsInfo.initialCompoundRate.toString(),
          principalAmount: rewardsInfo.principalAmount.toString(),
          shares: rewardsInfo.shares.toString(),
          stakedAt: stakerInfo.stakedAt.toString(),
          unlockedBoostingAmount: rewardsInfo.unlockedBoostingAmount.toString(),
        },
      }
    })

  const boostingInfos = await Promise.all(poolsCalls)

  return boostingInfos.reduce(
    (acc, { sousId, boostingInfo }) => ({
      ...acc,
      [sousId]: boostingInfo,
    }),
    {},
  )
}
